import { useQuery } from '@tanstack/react-query'
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { insightsApi, relatoriosApi } from '../../api/endpoints'
import { Card, CardHeader, CardBody, KpiCard } from '../../components/ui/Card'
import { LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { PageHeader } from '../../layout/PageHeader'
import { formatMoney, formatDate } from '../../lib/utils'

const CHART_TOOLTIP = { background: '#1B2330', border: '1px solid #2D3748', borderRadius: 4, fontSize: 12 }
const AXIS_TICK = { fontSize: 11, fill: '#8B949E' }
const kFormatter = (v: number | string) => `${(Number(v) / 1000).toFixed(0)}k`

export function InsightsPage() {
  return (
    <>
      <PageHeader title="Insights" />
      <div className="grid gap-6 xl:grid-cols-2">
        <TaxaPoupancaCard />
        <PatrimonioCard />
        <QualidadeCategorizacaoCard />
        <ParcelasFuturasCard />
      </div>
      <div className="mt-6">
        <AssinaturasDetectadasCard />
      </div>
    </>
  )
}

function TaxaPoupancaCard() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['insights-taxa-poupanca'],
    queryFn: () => {
      const hoje = new Date()
      return relatoriosApi.porMes(hoje.getMonth() + 1, hoje.getFullYear(), 12)
    },
  })

  const serie = data.map((m) => ({
    ...m,
    taxaPoupanca: m.receitas > 0 ? Math.round(((m.receitas - m.despesas) / m.receitas) * 1000) / 10 : 0,
  }))
  const ultimo = serie.at(-1)

  return (
    <Card>
      <CardHeader title="Taxa de poupança" />
      <CardBody>
        {isLoading ? <LoadingState /> : serie.every((s) => s.receitas === 0) ? (
          <EmptyState icon="ri-percent-line" message="Sem receitas no período pra calcular a taxa." />
        ) : (
          <>
            <p className="mb-3 text-13 text-muted">
              Quanto sobra das receitas depois das despesas, mês a mês.
              {ultimo && <> Neste mês: <span className={`font-semibold ${ultimo.taxaPoupanca >= 0 ? 'text-income' : 'text-expense'}`}>{ultimo.taxaPoupanca.toFixed(1)}%</span>.</>}
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={serie} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} contentStyle={CHART_TOOLTIP} />
                <Line type="monotone" dataKey="taxaPoupanca" name="Taxa de poupança" stroke="#22C55E" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </CardBody>
    </Card>
  )
}

function PatrimonioCard() {
  const { data, isLoading } = useQuery({ queryKey: ['insights-patrimonio'], queryFn: () => insightsApi.patrimonioHistorico(12) })

  return (
    <Card>
      <CardHeader title="Patrimônio ao longo do tempo" />
      <CardBody>
        {isLoading ? <LoadingState /> : !data || data.itens.length < 2 ? (
          <EmptyState icon="ri-line-chart-line" message="Ainda estamos começando a registrar o histórico — volte em alguns dias para ver a evolução." />
        ) : (
          <>
            {!data.historicoCompleto && (
              <p className="mb-3 text-13 text-muted"><i className="ri-information-line" aria-hidden /> O rastreamento começou há pouco tempo — o gráfico mostra só o período já registrado.</p>
            )}
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data.itens} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="patrimonioFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFB300" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FFB300" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={kFormatter} />
                <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} />
                <Area type="monotone" dataKey="valor" name="Patrimônio" stroke="#FFB300" strokeWidth={2.5} fill="url(#patrimonioFill)" dot={{ r: 3, fill: '#FFB300' }} />
              </AreaChart>
            </ResponsiveContainer>
          </>
        )}
      </CardBody>
    </Card>
  )
}

function QualidadeCategorizacaoCard() {
  const { data, isLoading } = useQuery({ queryKey: ['insights-qualidade-categorizacao'], queryFn: () => insightsApi.qualidadeCategorizacao(6) })

  return (
    <Card>
      <CardHeader title="Qualidade da categorização automática" />
      <CardBody>
        {isLoading ? <LoadingState /> : !data || data.itens.every((i) => i.total === 0) ? (
          <EmptyState icon="ri-price-tag-3-line" message="Sem transações da Pluggy no período." />
        ) : (
          <>
            <KpiCard title="Categorizadas automaticamente" value={`${data.percentualGeral.toFixed(0)}%`} icon="ri-magic-line" tone="info"
              footer={<p className="mt-3 text-2xs text-muted">Nos últimos 6 meses de transações vindas da Pluggy.</p>} />
            <ResponsiveContainer width="100%" height={180} className="mt-4">
              <BarChart data={data.itens} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} contentStyle={CHART_TOOLTIP} />
                <Bar dataKey="percentualCategorizado" name="% categorizado" fill="#3B82F6" radius={[3, 3, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </CardBody>
    </Card>
  )
}

function ParcelasFuturasCard() {
  const { data, isLoading } = useQuery({ queryKey: ['insights-parcelas-futuras'], queryFn: () => insightsApi.parcelasFuturas() })

  return (
    <Card>
      <CardHeader title="Peso de parcelas futuras" />
      <CardBody>
        {isLoading ? <LoadingState /> : !data || data.itens.length === 0 ? (
          <EmptyState icon="ri-bank-card-line" message="Nenhuma parcela de cartão em aberto." />
        ) : (
          <>
            <KpiCard title="Total ainda a pagar" value={formatMoney(data.totalGeral)} icon="ri-bank-card-2-line" tone="expense" />
            <ResponsiveContainer width="100%" height={180} className="mt-4">
              <BarChart data={data.itens} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={kFormatter} />
                <Tooltip formatter={(v) => formatMoney(Number(v))} contentStyle={CHART_TOOLTIP} />
                <Bar dataKey="total" name="Total de parcelas" fill="#EF4444" radius={[3, 3, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </CardBody>
    </Card>
  )
}

function AssinaturasDetectadasCard() {
  const { data = [], isLoading } = useQuery({ queryKey: ['insights-assinaturas'], queryFn: () => insightsApi.assinaturasDetectadas() })

  return (
    <Card>
      <CardHeader title="Assinaturas e recorrências detectadas" />
      {isLoading ? <LoadingState /> : data.length === 0 ? (
        <EmptyState icon="ri-repeat-line" message="Nenhum padrão recorrente detectado nos últimos 6 meses." />
      ) : (
        <Table>
          <THead><tr><Th>Descrição</Th><Th>Categoria</Th><Th className="text-right">Valor médio</Th><Th className="text-right">Ocorrências</Th><Th>Última vez</Th></tr></THead>
          <tbody>
            {data.map((a) => (
              <Tr key={a.descricao}>
                <Td className="font-medium text-white">{a.descricao}</Td>
                <Td className="text-muted">{a.categoriaNome ?? '—'}</Td>
                <Td className="whitespace-nowrap text-right font-semibold text-expense">{formatMoney(a.valorMedio)}</Td>
                <Td className="text-right">{a.ocorrencias}</Td>
                <Td className="whitespace-nowrap text-muted">{formatDate(a.ultimaData)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  )
}
